import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useLayout, getDensityClasses } from '../contexts/LayoutContext';

interface Dimension {
  id: number;
  name: string;
  description: string;
  questionCount: number;
  completedCount?: number;
}

interface QuestionnaireOverviewProps {
  dimensions: Dimension[];
  totalQuestions: number;
  completedQuestions: number;
  compact?: boolean;
  onDimensionClick?: (dimensionId: number, dimensionName: string) => void;
}

const QuestionnaireOverview: React.FC<QuestionnaireOverviewProps> = ({
  dimensions,
  totalQuestions,
  completedQuestions,
  compact = false,
  onDimensionClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { density } = useLayout();
  const densityClasses = getDensityClasses(density);

  const progressPercentage = totalQuestions > 0 ? (completedQuestions / totalQuestions) * 100 : 0;

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${densityClasses.card} ${compact ? 'py-1 px-3' : ''}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left hover:bg-gray-50 rounded-lg transition-colors"
      >
        <div className="flex-1">
          {!compact && (
            <h3 className={`font-semibold text-gray-900 ${densityClasses.heading}`}>
              Evaluation Overview
            </h3>
          )}
          <div className="mt-1">
            <div className="flex items-center space-x-4">
              <span className={`text-gray-600 ${densityClasses.text}`}>
                {completedQuestions} of {totalQuestions} questions completed
              </span>
              <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <span className={`text-sm text-gray-500 ${densityClasses.text}`}>
                {Math.round(progressPercentage)}%
              </span>
            </div>
          </div>
        </div>
        <div className="ml-4">
          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className={`mt-4 pt-4 border-t border-gray-200 ${densityClasses.spacing}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dimensions.map((dimension) => {
              const isFullyCompleted = dimension.completedCount !== undefined && dimension.completedCount === dimension.questionCount && dimension.questionCount > 0;
              return (
                <div
                  key={dimension.id}
                  onClick={() => onDimensionClick?.(dimension.id, dimension.name)}
                  className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 cursor-pointer transition-all hover:shadow-md border border-transparent hover:border-blue-200 text-left"
                >
                  <h4 className={`font-medium text-gray-900 ${densityClasses.text}`}>
                    {dimension.name}
                  </h4>
                  <p className={`text-gray-600 mt-1 line-clamp-2 ${densityClasses.text}`} title={dimension.description}>
                    {dimension.description}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className={`text-xs font-semibold ${densityClasses.text} ${isFullyCompleted ? 'text-green-600' : 'text-blue-600'}`}>
                      {dimension.completedCount !== undefined
                        ? `${dimension.completedCount} of ${dimension.questionCount} completed`
                        : `${dimension.questionCount} questions available`
                      }
                    </span>
                    {dimension.completedCount !== undefined && dimension.questionCount > 0 && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        isFullyCompleted
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-50 text-blue-800'
                      }`}>
                        {Math.round((dimension.completedCount / dimension.questionCount) * 100)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireOverview;
